import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSettings } from "@/contexts/SettingsContext";
import { printTicket, getStoredSettings } from "@/lib/ticketGenerator";
import MovementDetailDialog from "@/components/MovementDetailDialog";
import { 
  Wallet, 
  Plus, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Loader2,
  Calendar,
  Lock,
  Download,
  AlertTriangle,
  CheckCircle,
  RefreshCcw,
  Printer,
  History,
  Undo2,
  Search,
  CreditCard,
  Banknote,
  Filter,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Scale,
  Pencil,
  Eye,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCOME_CATEGORIES = [
  { value: "rental", label: "Alquiler" },
  { value: "accessory_sale", label: "Venta accesorios" },
  { value: "extension", label: "Ampliación alquiler" },
  { value: "other", label: "Otros" },
];

const EXPENSE_CATEGORIES = [
  { value: "purchase", label: "Compra material" },
  { value: "maintenance", label: "Mantenimiento" },
  { value: "supplies", label: "Suministros" },
  { value: "payroll", label: "Nóminas" },
  { value: "rent", label: "Alquiler local" },
  { value: "refund", label: "Devolución cliente" },
  { value: "other", label: "Otros" },
];

const PAYMENT_METHODS = [
  { value: "cash", label: "Efectivo", icon: Banknote },
  { value: "card", label: "Tarjeta", icon: CreditCard },
];

export default function CashRegister() {
  const { darkMode } = useSettings();
  
  // State (removed date state - always use today)
  const [summary, setSummary] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Privacy visibility state - all hidden by default (accordion style)
  const [visibleMetrics, setVisibleMetrics] = useState({
    ingresos: false,
    gastos: false,
    balance: false,
    efectivo: false,
    tarjeta: false,
    fondo: false
  });
  
  // Master visibility toggle - show/hide all at once
  const [allVisible, setAllVisible] = useState(false);
  
  // Toggle visibility of a specific metric
  const toggleMetricVisibility = (metric) => {
    setVisibleMetrics(prev => ({ ...prev, [metric]: !prev[metric] }));
  };
  
  // Toggle all metrics visibility at once (master toggle)
  const toggleAllVisibility = () => {
    const newState = !allVisible;
    setAllVisible(newState);
    setVisibleMetrics({
      ingresos: newState,
      gastos: newState,
      balance: newState,
      efectivo: newState,
      tarjeta: newState,
      fondo: newState
    });
  };
  
  // Format value for display - show masked or real value with smooth transition
  const formatValue = (value, isVisible) => {
    const formatted = typeof value === 'number' ? formatCurrency(Math.abs(value)) : value;
    return isVisible ? formatted : '••••••';
  };
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showOpenSessionDialog, setShowOpenSessionDialog] = useState(false);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  
  // Edit payment method dialog
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [editingMovement, setEditingMovement] = useState(null);
  const [newPaymentMethod, setNewPaymentMethod] = useState("");
  
  // Session state
  const [activeSession, setActiveSession] = useState(null);
  const [openingBalance, setOpeningBalance] = useState("");
  const [sessionNotes, setSessionNotes] = useState("");
  
  // Movement form
  const [movementType, setMovementType] = useState("income");
  const [newMovement, setNewMovement] = useState({
    amount: "",
    payment_method: "cash",
    category: "",
    concept: "",
    notes: ""
  });
  
  // Arqueo form
  const [arqueoForm, setArqueoForm] = useState({
    physical_cash: "",
    card_total: "",
    notes: ""
  });
  
  // Tabs
  const [activeTab, setActiveTab] = useState("today");
  const [closureHistory, setClosureHistory] = useState([]);
  const [filteredClosures, setFilteredClosures] = useState([]);
  const [historicLoading, setHistoricLoading] = useState(false);
  const [revertClosureId, setRevertClosureId] = useState(null);
  
  // Filtro de cierres - por defecto últimos 7 días
  const [closureFilterType, setClosureFilterType] = useState("week"); // "today", "week", "month", "range"
  const [closureFilterFrom, setClosureFilterFrom] = useState(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [closureFilterTo, setClosureFilterTo] = useState(
    new Date().toISOString().split('T')[0]
  );
  
  // ============ HISTORIAL / BUSCADOR STATE ============
  const [historySearch, setHistorySearch] = useState({
    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    dateTo: new Date().toISOString().split('T')[0],
    query: "",
    paymentMethod: "all"
  });
  const [historyResults, setHistoryResults] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const HISTORY_PAGE_SIZE = 20;
  
  // Detail modal for history
  const [showHistoryDetailDialog, setShowHistoryDetailDialog] = useState(false);
  const [selectedHistoryMovement, setSelectedHistoryMovement] = useState(null);

  useEffect(() => {
    loadData();
  }, []); // Load data once on mount

  useEffect(() => {
    const interval = setInterval(() => {
      // Actualización silenciosa en background sin mostrar loading
      if (activeTab === "today") {
        loadDataSilently();
      }
    }, 30000); // Aumentado a 30 segundos
    return () => clearInterval(interval);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "closures") loadClosureHistory();
  }, [activeTab]);

  // Filtrar cierres cuando cambian los filtros o los datos
  useEffect(() => {
    if (!closureHistory || closureHistory.length === 0) {
      setFilteredClosures([]);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let fromDate, toDate;
    
    switch (closureFilterType) {
      case "today":
        fromDate = today;
        toDate = today;
        break;
      case "week":
        fromDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        toDate = today;
        break;
      case "month":
        fromDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        toDate = today;
        break;
      case "range":
        fromDate = new Date(closureFilterFrom);
        toDate = new Date(closureFilterTo);
        break;
      default:
        fromDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        toDate = today;
    }
    
    // Asegurar que toDate incluya todo el día (hasta 23:59:59)
    toDate.setHours(23, 59, 59, 999);
    fromDate.setHours(0, 0, 0, 0);
    
    const filtered = closureHistory.filter(closure => {
      // Normalizar la fecha del cierre - extraer solo YYYY-MM-DD
      const closureDateStr = closure.date || closure.closed_at || '';
      const closureDatePart = closureDateStr.substring(0, 10); // YYYY-MM-DD
      const closureDate = new Date(closureDatePart + 'T12:00:00'); // Usar mediodía para evitar problemas de timezone
      
      return closureDate >= fromDate && closureDate <= toDate;
    });
    
    // Ordenar por fecha descendente
    filtered.sort((a, b) => {
      const dateA = new Date(a.date || a.closed_at || '');
      const dateB = new Date(b.date || b.closed_at || '');
      return dateB - dateA;
    });
    
    setFilteredClosures(filtered);
  }, [closureHistory, closureFilterType, closureFilterFrom, closureFilterTo]);

  // Handler para cambio rápido de filtro
  const handleClosureFilterTypeChange = (type) => {
    setClosureFilterType(type);
    const today = new Date();
    
    switch (type) {
      case "today":
        setClosureFilterFrom(today.toISOString().split('T')[0]);
        setClosureFilterTo(today.toISOString().split('T')[0]);
        break;
      case "week":
        setClosureFilterFrom(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
        setClosureFilterTo(today.toISOString().split('T')[0]);
        break;
      case "month":
        setClosureFilterFrom(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
        setClosureFilterTo(today.toISOString().split('T')[0]);
        break;
      case "range":
        // Keep current dates for manual selection
        break;
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0]; // Always use today's date
      const [summaryRes, movementsRes, sessionRes] = await Promise.all([
        axios.get(`${API}/cash/summary/realtime`, { params: { date: today } }),
        axios.get(`${API}/cash/movements`, { params: { date: today } }),
        axios.get(`${API}/cash/sessions/active`)
      ]);
      setSummary(summaryRes.data);
      // Dedup: filter movements with same dedup_key (extension duplicates from old code)
      const seenKeys = new Set();
      const dedupedMovements = (movementsRes.data || []).filter(m => {
        if (m.dedup_key) {
          if (seenKeys.has(m.dedup_key)) return false;
          seenKeys.add(m.dedup_key);
        }
        return true;
      });
      setMovements(dedupedMovements);
      setActiveSession(sessionRes.data);
    } catch (error) {
      toast.error("Error al cargar datos de caja");
    } finally {
      setLoading(false);
    }
  };

  // Actualización silenciosa sin mostrar loading (para auto-refresh)
  const loadDataSilently = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [summaryRes, movementsRes, sessionRes] = await Promise.all([
        axios.get(`${API}/cash/summary/realtime`, { params: { date: today } }),
        axios.get(`${API}/cash/movements`, { params: { date: today } }),
        axios.get(`${API}/cash/sessions/active`)
      ]);
      setSummary(summaryRes.data);
      const seenKeys2 = new Set();
      const dedupedMov2 = (movementsRes.data || []).filter(m => {
        if (m.dedup_key) {
          if (seenKeys2.has(m.dedup_key)) return false;
          seenKeys2.add(m.dedup_key);
        }
        return true;
      });
      setMovements(dedupedMov2);
      setActiveSession(sessionRes.data);
    } catch (error) {
      // Silenciar error en actualizaciones automáticas
      console.error("Error en actualización silenciosa:", error);
    }
  };

  const openCashSession = async () => {
    if (!openingBalance || parseFloat(openingBalance) < 0) {
      toast.error("Introduce un fondo de caja válido (puede ser 0)");
      return;
    }
    try {
      await axios.post(`${API}/cash/sessions/open`, {
        opening_balance: parseFloat(openingBalance),
        notes: sessionNotes
      });
      toast.success("Caja abierta correctamente");
      setShowOpenSessionDialog(false);
      setOpeningBalance("");
      setSessionNotes("");
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al abrir caja");
    }
  };

  const loadClosureHistory = async () => {
    setHistoricLoading(true);
    try {
      const res = await axios.get(`${API}/cash/closings`);
      setClosureHistory(res.data);
    } catch (error) {
      toast.error("Error al cargar historial de cierres");
    } finally {
      setHistoricLoading(false);
    }
  };

  // ============ BÚSQUEDA HISTÓRICA ============
  const searchHistory = async (page = 1) => {
    setHistoryLoading(true);
    setHistoryPage(page);
    try {
      const params = new URLSearchParams({
        date_from: historySearch.dateFrom,
        date_to: historySearch.dateTo,
        skip: ((page - 1) * HISTORY_PAGE_SIZE).toString(),
        limit: HISTORY_PAGE_SIZE.toString()
      });
      
      if (historySearch.query.trim()) {
        params.append('search', historySearch.query.trim());
      }
      if (historySearch.paymentMethod !== 'all') {
        params.append('payment_method', historySearch.paymentMethod);
      }
      
      const res = await axios.get(`${API}/cash/movements/search?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Dedup: group movements with same dedup_key (extensions recorded twice by old code)
      const rawResults = res.data.results || [];
      const seen = new Set();
      const deduped = rawResults.filter(m => {
        if (m.dedup_key) {
          if (seen.has(m.dedup_key)) return false;
          seen.add(m.dedup_key);
        }
        return true;
      });
      
      setHistoryResults(deduped);
      setHistoryTotal(res.data.total || 0);
    } catch (error) {
      console.error("Error searching history:", error);
      toast.error("Error al buscar en el historial");
      setHistoryResults([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryDetail = (movement) => {
    setSelectedHistoryMovement(movement);
    setShowHistoryDetailDialog(true);
  };

  const reprintHistoryTicket = async (movement) => {
    // Reutilizar la misma lógica que printMovementTicket para consistencia total
    await printMovementTicket(movement);
  };

  const createMovement = async () => {
    if (!newMovement.amount || !newMovement.concept || !newMovement.category) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }
    try {
      await axios.post(`${API}/cash/movements`, {
        movement_type: movementType,
        amount: parseFloat(newMovement.amount),
        payment_method: newMovement.payment_method,
        category: newMovement.category,
        concept: newMovement.concept,
        notes: newMovement.notes
      });
      toast.success(movementType === "income" ? "Entrada registrada" : "Salida registrada");
      setShowAddDialog(false);
      setNewMovement({ amount: "", payment_method: "cash", category: "", concept: "", notes: "" });
      loadData();
    } catch (error) {
      toast.error("Error al registrar movimiento");
    }
  };

  // Cálculo de descuadres basado en los nuevos campos del backend
  const getDiscrepancy = () => {
    const realCash = parseFloat(arqueoForm.physical_cash) || 0;
    const realCard = parseFloat(arqueoForm.card_total) || 0;
    const expectedCash = summary?.efectivo_esperado || 0;
    const expectedCard = summary?.tarjeta_esperada || 0;
    
    return {
      cash: realCash - expectedCash,
      card: realCard - expectedCard,
      total: (realCash - expectedCash) + (realCard - expectedCard)
    };
  };

  // ============ IMPRIMIR TICKET DE MOVIMIENTO (USA GENERADOR MAESTRO) ============
  const printMovementTicket = async (movement) => {
    // Obtener etiqueta de categoría
    const allCategories = [...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES];
    const categoryLabel = allCategories.find(c => c.value === movement.category)?.label || movement.category || '-';
    
    // Determinar el tipo de ticket según la categoría y tipo de movimiento
    const isRentalCategory = movement.category === 'rental';
    let rentalItems = movement.rental_items || [];
    let rentalDays = movement.rental_days;
    let startDate = movement.rental_start_date;
    let endDate = movement.rental_end_date;
    
    // FALLBACK: Si es un alquiler pero no tiene items guardados, intentar recuperarlos del alquiler original
    if (isRentalCategory && rentalItems.length === 0 && movement.reference_id) {
      try {
        const response = await axios.get(`${API}/rentals/${movement.reference_id}`);
        if (response.data) {
          const rental = response.data;
          // Mapear items del alquiler
          rentalItems = (rental.items || []).map(item => ({
            name: `${item.item_type || 'Artículo'} ${item.brand || ''}`.trim(),
            size: item.size || '',
            internal_code: item.internal_code || item.barcode || '',
            days: rental.days || 1,
            subtotal: item.subtotal || 0,
            item_type: item.item_type
          }));
          rentalDays = rental.days;
          startDate = rental.start_date;
          endDate = rental.end_date;
        }
      } catch (error) {
        console.log('No se pudo recuperar items del alquiler original:', error);
        // Continuar con ticket de movimiento simple
      }
    }
    
    const hasRentalItems = rentalItems.length > 0;
    
    // Preparar datos para el generador maestro
    const ticketData = {
      operationNumber: movement.operation_number,
      date: movement.created_at,
      createdAt: movement.created_at,
      movementType: movement.movement_type,
      category: movement.category,
      categoryLabel: categoryLabel,
      concept: movement.concept,
      notes: movement.notes,
      amount: movement.amount,
      paymentMethod: movement.payment_method,
      customerName: movement.customer_name,
      customer: movement.customer_name,
      // Datos de alquiler (si existen)
      items: rentalItems,
      days: rentalDays || null,
      startDate: startDate || null,
      endDate: endDate || null,
      total: movement.amount
    };
    
    // Usar generador maestro - tipo 'rental' si tiene items, sino 'movement'
    const ticketType = (isRentalCategory && hasRentalItems) ? 'rental' : 'movement';
    
    printTicket({
      ticketType: ticketType,
      data: ticketData
    }).catch(err => {
      console.error('[CashRegister] Error al imprimir ticket de movimiento:', err);
      toast.error("No se pudo abrir ventana de impresión. Permite los popups.");
    });
  };

  // ============ EDITAR MÉTODO DE PAGO ============
  const openEditPaymentDialog = (movement) => {
    setEditingMovement(movement);
    setNewPaymentMethod(movement.payment_method);
    setShowEditPaymentDialog(true);
  };

  const updatePaymentMethod = async () => {
    if (!editingMovement || !newPaymentMethod) return;
    
    try {
      await axios.patch(`${API}/cash/movements/${editingMovement.id}`, {
        payment_method: newPaymentMethod
      });
      
      toast.success("Método de pago actualizado");
      setShowEditPaymentDialog(false);
      setEditingMovement(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    }
  };

  const closeCashRegister = async () => {
    if (arqueoForm.physical_cash === "" && arqueoForm.physical_cash !== 0) {
      toast.error("Debes introducir el efectivo contado (puede ser 0)");
      return;
    }

    const disc = getDiscrepancy();
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const closingData = {
        date: today,
        physical_cash: parseFloat(arqueoForm.physical_cash) || 0,
        card_total: parseFloat(arqueoForm.card_total) || 0,
        expected_cash: summary?.efectivo_esperado || 0,
        expected_card: summary?.tarjeta_esperada || 0,
        discrepancy_cash: disc.cash,
        discrepancy_card: disc.card,
        discrepancy_total: disc.total,
        notes: arqueoForm.notes
      };
      
      const response = await axios.post(`${API}/cash/close`, closingData);
      printClosingTicket({ ...closingData, ...response.data, ...summary });
      
      toast.success("Caja cerrada correctamente");
      setShowCloseDialog(false);
      setArqueoForm({ physical_cash: "", card_total: "", notes: "" });
      loadData();
      loadClosureHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cerrar caja");
    }
  };

  // ============ IMPRIMIR TICKET DE CIERRE (USA GENERADOR MAESTRO) ============
  const printClosingTicket = (data) => {
    // Calcular descuadres usando la misma lógica del modal
    const realCash = parseFloat(data.physical_cash) || 0;
    const realCard = parseFloat(data.card_total) || 0;
    const expectedCash = data.efectivo_esperado || 0;
    const expectedCard = data.tarjeta_esperada || 0;
    const discCash = realCash - expectedCash;
    const discCard = realCard - expectedCard;
    const discTotal = discCash + discCard;
    
    // Preparar datos para el generador maestro
    const ticketData = {
      date: data.date,
      closureNumber: data.closure_number || 1,
      shiftNumber: data.closure_number || 1,
      closedBy: data.closed_by || 'N/A',
      openingBalance: data.opening_balance || 0,
      totalIncome: data.ingresos_brutos || 0,
      ingresosBrutos: data.ingresos_brutos || 0,
      totalRefunds: data.total_salidas || 0,
      totalExpense: data.total_gastos || 0,
      devoluciones: data.total_salidas || 0,
      gastos: data.total_gastos || 0,
      netIncome: data.balance_neto_dia || 0,
      balanceNeto: data.balance_neto_dia || 0,
      expectedCash: expectedCash,
      efectivoEsperado: expectedCash,
      countedCash: realCash,
      physicalCash: realCash,
      expectedCard: expectedCard,
      tarjetaEsperada: expectedCard,
      countedCard: realCard,
      cardTotal: realCard,
      discrepancyCash: discCash,
      discrepancyCard: discCard,
      discrepancyTotal: discTotal,
      totalOperations: data.movements_count || 0,
      notes: data.notes
    };
    
    // Usar generador maestro
    printTicket({
      ticketType: 'closing',
      data: ticketData
    }).catch(err => {
      console.error('[CashRegister] Error al imprimir ticket de cierre:', err);
      toast.error("No se pudo abrir ventana de impresión. Permite los popups.");
    });
  };

  const confirmRevertClosure = async () => {
    if (!revertClosureId) return;
    try {
      await axios.delete(`${API}/cash/closings/${revertClosureId}`);
      toast.success("Cierre reabierto correctamente");
      loadClosureHistory();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reabrir caja");
    } finally {
      setRevertClosureId(null);
    }
  };

  const exportToCSV = () => {
    if (movements.length === 0) {
      toast.error("No hay movimientos para exportar");
      return;
    }
    const headers = "Fecha,Hora,Tipo,Concepto,Método,Importe\n";
    const rows = movements.map(m => {
      const dateStr = m.created_at.split('T')[0];
      const time = m.created_at.split('T')[1].substring(0, 5);
      const type = m.movement_type === 'income' ? 'Entrada' : m.movement_type === 'refund' ? 'Devolución' : 'Salida';
      return `${dateStr},${time},${type},"${m.concept}",${m.payment_method},${m.amount}`;
    }).join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    a.download = `caja_${today}.csv`;
    a.click();
    toast.success("Exportado correctamente");
  };

  const getMovementTypeBadge = (type) => {
    switch(type) {
      case 'income': return <Badge className="bg-emerald-600 text-white border-0">Entrada</Badge>;
      case 'expense': return <Badge className="bg-red-500 text-white border-0">Salida</Badge>;
      case 'refund': return <Badge className="bg-orange-600 text-white border-0">Devolución</Badge>;
      default: return <Badge variant="outline">{type}</Badge>;
    }
  };

  const disc = getDiscrepancy();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={`p-6 lg:p-8 space-y-6 min-h-screen ${darkMode ? 'bg-[#121212]' : 'bg-slate-50'}`} data-testid="cash-register">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            <Wallet className="inline-block h-8 w-8 mr-2" />
            Gestión de Caja
          </h1>
          <p className={`mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
            Control financiero diario - {new Date().toLocaleDateString('es-ES', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="today" className="flex items-center gap-2">
            <Wallet className="h-4 w-4" />
            Caja Diaria
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Historial
          </TabsTrigger>
          <TabsTrigger value="closures" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Cierres
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="space-y-3">
          {/* ============ BOTÓN MAESTRO DE VISIBILIDAD ============ */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleAllVisibility}
              className={`gap-2 ${allVisible ? 'bg-emerald-500/10 border-emerald-300 text-emerald-700 hover:bg-emerald-500/20' : ''}`}
              data-testid="toggle-all-visibility"
            >
              {allVisible ? (
                <>
                  <EyeOff className="h-4 w-4" />
                  Ocultar Todo
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4" />
                  Mostrar Todo
                </>
              )}
            </Button>
          </div>

          {/* ============ PANEL SUPERIOR: 3 KPIs (Sin Blur - Sistema Acordeón) ============ */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* KPI 1: Ingresos Brutos */}
            <Card className={`${darkMode ? 'bg-[#1a1a1a] border-[#333]' : 'border-slate-200'} transition-all duration-200`} data-testid="kpi-ingresos">
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                        Ingresos
                      </p>
                      <button 
                        onClick={() => toggleMetricVisibility('ingresos')}
                        className={`p-1.5 rounded-md transition-all duration-200 ${
                          visibleMetrics.ingresos 
                            ? 'bg-emerald-500/20 text-emerald-600 hover:bg-emerald-200 hover:text-emerald-800' 
                            : `${darkMode ? 'hover:bg-slate-700 text-slate-600 hover:text-slate-200' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-600'}`
                        }`}
                        title={visibleMetrics.ingresos ? 'Ocultar' : 'Mostrar'}
                      >
                        {visibleMetrics.ingresos ? (
                          <Eye className="h-3.5 w-3.5" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    <p className={`text-2xl font-bold transition-all duration-300 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {visibleMetrics.ingresos ? (
                        `${formatCurrency((summary?.ingresos_brutos || 0))}`
                      ) : (
                        <span className="text-slate-600 tracking-wider">••••••</span>
                      )}
                    </p>
                  </div>
                  <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${darkMode ? 'bg-emerald-900/30' : 'bg-emerald-500/20'}`}>
                    <TrendingUp className={`h-5 w-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* KPI 2: Salidas y Devoluciones */}
            <Card className={`${darkMode ? 'bg-[#1a1a1a] border-[#333]' : 'border-slate-200'} transition-all duration-200`} data-testid="kpi-salidas">
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                        Salidas
                      </p>
                      <button 
                        onClick={() => toggleMetricVisibility('gastos')}
                        className={`p-1.5 rounded-md transition-all duration-200 ${
                          visibleMetrics.gastos 
                            ? 'bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800' 
                            : `${darkMode ? 'hover:bg-slate-700 text-slate-600 hover:text-slate-200' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-600'}`
                        }`}
                        title={visibleMetrics.gastos ? 'Ocultar' : 'Mostrar'}
                      >
                        {visibleMetrics.gastos ? (
                          <Eye className="h-3.5 w-3.5" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    <p className={`text-2xl font-bold text-red-600 transition-all duration-300`}>
                      {visibleMetrics.gastos ? (
                        `-${formatCurrency((summary?.total_salidas || 0))}`
                      ) : (
                        <span className="text-slate-600 tracking-wider">••••••</span>
                      )}
                    </p>
                  </div>
                  <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${darkMode ? 'bg-red-900/30' : 'bg-red-100'}`}>
                    <TrendingDown className={`h-5 w-5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* KPI 3: Balance Neto del Día */}
            <Card className={`border-2 ${(summary?.balance_neto_dia || 0) >= 0 ? 'border-emerald-300 bg-emerald-500/10' : 'border-red-300 bg-red-50'} ${darkMode ? 'bg-opacity-10' : ''} transition-all duration-200`} data-testid="kpi-balance">
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Balance Neto
                      </p>
                      <button 
                        onClick={() => toggleMetricVisibility('balance')}
                        className={`p-1.5 rounded-md transition-all duration-200 ${
                          visibleMetrics.balance 
                            ? `${(summary?.balance_neto_dia || 0) >= 0 ? 'bg-emerald-200 text-emerald-700' : 'bg-red-200 text-red-700'} hover:opacity-80` 
                            : 'hover:bg-white/50 text-slate-500 hover:text-slate-700'
                        }`}
                        title={visibleMetrics.balance ? 'Ocultar' : 'Mostrar'}
                      >
                        {visibleMetrics.balance ? (
                          <Eye className="h-3.5 w-3.5" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    <p className={`text-2xl font-bold transition-all duration-300 ${(summary?.balance_neto_dia || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {visibleMetrics.balance ? (
                        `${formatCurrency((summary?.balance_neto_dia || 0))}`
                      ) : (
                        <span className="text-slate-600 tracking-wider">••••••</span>
                      )}
                    </p>
                  </div>
                  <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${(summary?.balance_neto_dia || 0) >= 0 ? 'bg-emerald-200' : 'bg-red-200'}`}>
                    <Scale className={`h-5 w-5 ${(summary?.balance_neto_dia || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ============ PANEL SECUNDARIO: ARQUEO (Sin Blur - Sistema Acordeón) ============ */}
          <Card className={`${darkMode ? 'bg-[#1a1a1a] border-[#333]' : 'border-slate-200'}`} data-testid="arqueo-panel">
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-base flex items-center gap-2">
                <Banknote className="h-4 w-4" />
                Desglose para Arqueo
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-5 pb-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {/* Fondo Inicial */}
                <div className={`p-3 rounded-lg transition-all duration-200 ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                  <div className="flex items-center gap-1.5">
                    <p className={`text-xs font-medium ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                      Fondo Inicial
                    </p>
                    <button 
                      onClick={() => toggleMetricVisibility('fondo')}
                      className={`p-1 rounded-md transition-all duration-200 ${
                        visibleMetrics.fondo 
                          ? 'bg-slate-200 text-slate-600' 
                          : 'hover:bg-slate-200/50 text-slate-600 hover:text-slate-600'
                      }`}
                    >
                      {visibleMetrics.fondo ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    </button>
                  </div>
                  <p className={`text-lg font-bold mt-0.5 transition-all duration-300 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    {visibleMetrics.fondo ? (
                      `${formatCurrency((summary?.opening_balance || 0))}`
                    ) : (
                      <span className="text-slate-600 tracking-wider text-base">••••</span>
                    )}
                  </p>
                </div>

                {/* Efectivo Esperado en Cajón */}
                <div className={`p-3 rounded-lg border-2 transition-all duration-200 ${darkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-500/10 border-blue-200'}`}>
                  <div className="flex items-center gap-1.5">
                    <Banknote className={`h-3.5 w-3.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    <p className={`text-xs font-bold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      EFECTIVO
                    </p>
                    <button 
                      onClick={() => toggleMetricVisibility('efectivo')}
                      className={`p-1 rounded-md transition-all duration-200 ${
                        visibleMetrics.efectivo 
                          ? 'bg-blue-200 text-blue-600' 
                          : 'hover:bg-blue-200/50 text-blue-400 hover:text-blue-700'
                      }`}
                    >
                      {visibleMetrics.efectivo ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    </button>
                  </div>
                  <p className={`text-lg font-bold transition-all duration-300 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                    {visibleMetrics.efectivo ? (
                      `${formatCurrency((summary?.efectivo_esperado || 0))}`
                    ) : (
                      <span className="text-blue-300 tracking-wider text-base">••••</span>
                    )}
                  </p>
                </div>

                {/* Total Tarjeta */}
                <div className={`p-3 rounded-lg border-2 transition-all duration-200 ${darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-500/10 border-purple-200'}`}>
                  <div className="flex items-center gap-1.5">
                    <CreditCard className={`h-3.5 w-3.5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                    <p className={`text-xs font-bold ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                      TARJETA
                    </p>
                    <button 
                      onClick={() => toggleMetricVisibility('tarjeta')}
                      className={`p-1 rounded-md transition-all duration-200 ${
                        visibleMetrics.tarjeta 
                          ? 'bg-purple-200 text-purple-600' 
                          : 'hover:bg-purple-200/50 text-purple-400 hover:text-purple-700'
                      }`}
                    >
                      {visibleMetrics.tarjeta ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    </button>
                  </div>
                  <p className={`text-lg font-bold transition-all duration-300 ${(summary?.tarjeta_esperada || 0) >= 0 ? (darkMode ? 'text-purple-300' : 'text-purple-800') : 'text-red-600'}`}>
                    {visibleMetrics.tarjeta ? (
                      `${(summary?.tarjeta_esperada || 0) < 0 ? '-' : ''}${formatCurrency(Math.abs(summary?.tarjeta_esperada || 0))}`
                    ) : (
                      <span className="text-purple-300 tracking-wider text-base">••••</span>
                    )}
                  </p>
                </div>

                {/* Movimientos count */}
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                  <p className={`text-xs font-medium ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                    Movimientos
                  </p>
                  <p className={`text-lg font-bold mt-0.5 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    {summary?.movements_count || 0}
                  </p>
                </div>

                {/* Session status */}
                <div className={`p-3 rounded-lg ${activeSession ? 'bg-emerald-500/20 border border-emerald-300' : 'bg-amber-500/20 border border-amber-300'}`}>
                  <p className={`text-xs font-medium ${activeSession ? 'text-emerald-700' : 'text-amber-700'}`}>
                    Estado
                  </p>
                  <p className={`text-lg font-bold mt-0.5 ${activeSession ? 'text-emerald-800' : 'text-amber-800'}`}>
                    {activeSession ? '✓ Abierta' : '⚠ Cerrada'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Session Status + Actions */}
          {!activeSession ? (
            <Card className="border-2 border-amber-300 bg-amber-500/10">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-amber-900 text-sm">No hay caja abierta</p>
                    <p className="text-xs text-amber-800">
                      Los cobros sin caja abierta no se registrarán.
                    </p>
                  </div>
                  <Button 
                    onClick={() => setShowOpenSessionDialog(true)}
                    className="bg-amber-600 hover:bg-amber-700"
                    size="sm"
                    data-testid="open-cash-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Abrir Caja
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className={`border ${darkMode ? 'bg-emerald-900/20 border-emerald-700' : 'bg-emerald-500/10 border-emerald-200'}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className={`h-5 w-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <div>
                      <p className={`font-semibold ${darkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                        ✅ Caja abierta - Turno #{activeSession.session_number}
                      </p>
                      <p className={`text-sm ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                        Abierta por {activeSession.opened_by} el {new Date(activeSession.opened_at).toLocaleString('es-ES')}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => { setMovementType("income"); setShowAddDialog(true); }} data-testid="new-income-btn">
                      <ArrowUpRight className="h-4 w-4 mr-2" />
                      Entrada
                    </Button>
                    <Button variant="outline" onClick={() => { setMovementType("expense"); setShowAddDialog(true); }} data-testid="new-expense-btn">
                      <ArrowDownLeft className="h-4 w-4 mr-2" />
                      Salida
                    </Button>
                    <Button variant="outline" onClick={exportToCSV}>
                      <Download className="h-4 w-4 mr-2" />
                      CSV
                    </Button>
                    <Button variant="destructive" onClick={() => setShowCloseDialog(true)} data-testid="close-cash-btn">
                      <Lock className="h-4 w-4 mr-2" />
                      Cerrar Caja
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Movements Table */}
          <Card className={`${darkMode ? 'bg-[#1a1a1a] border-[#333]' : 'border-slate-200'}`}>
            <CardHeader className="py-3 px-5">
              <CardTitle className={`text-base flex items-center justify-between ${darkMode ? 'text-white' : ''}`}>
                <span className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Movimientos del Día ({movements.length})
                </span>
                {movements.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Últimos registros
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-5 pb-4">
              {movements.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                  <Wallet className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No hay movimientos registrados</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nº Ticket</TableHead>
                        <TableHead>Hora</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Concepto</TableHead>
                        <TableHead>Método</TableHead>
                        <TableHead className="text-right">Importe</TableHead>
                        <TableHead className="text-center">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movements.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell className="font-mono text-sm font-bold text-blue-600">
                            {m.operation_number || '-'}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {m.created_at.split('T')[1]?.substring(0, 5) || '-'}
                          </TableCell>
                          <TableCell>{getMovementTypeBadge(m.movement_type)}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{m.concept}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {PAYMENT_METHODS.find(p => p.value === m.payment_method)?.label || m.payment_method}
                            </Badge>
                          </TableCell>
                          <TableCell className={`text-right font-bold ${
                            m.movement_type === 'income' ? 'text-emerald-600' : 
                            m.movement_type === 'refund' ? 'text-orange-600' : 'text-red-600'
                          }`}>
                            {m.movement_type === 'income' ? '+' : '-'}{formatCurrency(m.amount)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditPaymentDialog(m)}
                                title="Editar método de pago"
                                data-testid={`edit-payment-${m.id}`}
                              >
                                <Pencil className="h-4 w-4 text-slate-500 hover:text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => printMovementTicket(m)}
                                title="Imprimir ticket"
                                data-testid={`print-ticket-${m.id}`}
                              >
                                <Printer className="h-4 w-4 text-slate-500 hover:text-emerald-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: HISTORIAL / BUSCADOR */}
        <TabsContent value="history" className="space-y-4">
          {/* Panel de Filtros */}
          <Card className={`${darkMode ? 'bg-[#1a1a1a] border-[#333]' : 'border-slate-200'}`}>
            <CardHeader className="py-4">
              <CardTitle className={`text-lg flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                <Search className="h-5 w-5" />
                Buscador de Tickets y Movimientos
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                {/* Fecha Desde */}
                <div>
                  <Label className="text-sm">Desde</Label>
                  <Input
                    type="date"
                    value={historySearch.dateFrom}
                    onChange={(e) => setHistorySearch(prev => ({ ...prev, dateFrom: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                {/* Fecha Hasta */}
                <div>
                  <Label className="text-sm">Hasta</Label>
                  <Input
                    type="date"
                    value={historySearch.dateTo}
                    onChange={(e) => setHistorySearch(prev => ({ ...prev, dateTo: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                {/* Búsqueda de Texto */}
                <div className="md:col-span-2">
                  <Label className="text-sm">Buscar (Cliente, Ticket, Concepto)</Label>
                  <Input
                    placeholder="Ej: Juan García, A000123, Alquiler..."
                    value={historySearch.query}
                    onChange={(e) => setHistorySearch(prev => ({ ...prev, query: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && searchHistory(1)}
                    className="mt-1"
                  />
                </div>
                
                {/* Botón Buscar */}
                <div>
                  <Button 
                    onClick={() => searchHistory(1)}
                    disabled={historyLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {historyLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Search className="h-4 w-4 mr-2" />
                    )}
                    Buscar
                  </Button>
                </div>
              </div>
              
              {/* Filtro de Método de Pago */}
              <div className="flex gap-2 mt-4">
                <Button 
                  variant={historySearch.paymentMethod === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setHistorySearch(prev => ({ ...prev, paymentMethod: 'all' }))}
                >
                  Todos
                </Button>
                <Button 
                  variant={historySearch.paymentMethod === 'cash' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setHistorySearch(prev => ({ ...prev, paymentMethod: 'cash' }))}
                  className={historySearch.paymentMethod === 'cash' ? 'bg-emerald-600' : ''}
                >
                  <Banknote className="h-4 w-4 mr-1" />
                  Efectivo
                </Button>
                <Button 
                  variant={historySearch.paymentMethod === 'card' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setHistorySearch(prev => ({ ...prev, paymentMethod: 'card' }))}
                  className={historySearch.paymentMethod === 'card' ? 'bg-purple-600' : ''}
                >
                  <CreditCard className="h-4 w-4 mr-1" />
                  Tarjeta
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card className={`${darkMode ? 'bg-[#1a1a1a] border-[#333]' : 'border-slate-200'}`}>
            <CardHeader className="py-3 px-5">
              <CardTitle className={`text-base flex items-center justify-between ${darkMode ? 'text-white' : ''}`}>
                <span className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Resultados
                  {historyTotal > 0 && (
                    <Badge variant="secondary">{historyTotal} encontrados</Badge>
                  )}
                </span>
                {historyResults.length > 0 && (
                  <span className="text-sm font-normal text-slate-500">
                    Página {historyPage} de {Math.ceil(historyTotal / HISTORY_PAGE_SIZE)}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {historyLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : historyResults.length === 0 ? (
                <div className={`text-center py-12 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                  <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Usa los filtros y pulsa Buscar para encontrar movimientos</p>
                  <p className="text-sm mt-1">Puedes buscar por nombre de cliente, número de ticket o concepto</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[140px]">Fecha/Hora</TableHead>
                          <TableHead className="w-[100px]">ID Ticket</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Concepto</TableHead>
                          <TableHead className="w-[80px]">Método</TableHead>
                          <TableHead className="w-[100px] text-right">Importe</TableHead>
                          <TableHead className="w-[80px]">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historyResults.map((mov) => (
                          <TableRow 
                            key={mov.id} 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => openHistoryDetail(mov)}
                          >
                            <TableCell className="font-mono text-xs">
                              {new Date(mov.created_at).toLocaleDateString('es-ES')}
                              <br />
                              <span className="text-slate-600">
                                {new Date(mov.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </TableCell>
                            <TableCell className="font-mono text-xs font-bold text-blue-600">
                              {mov.operation_number || `#${mov.id?.substring(0, 8)}`}
                            </TableCell>
                            <TableCell className="font-medium">
                              {mov.customer_name || '-'}
                              {mov.customer_dni && (
                                <span className="text-xs text-slate-600 ml-2">({mov.customer_dni})</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">
                              {mov.concept || mov.description || '-'}
                            </TableCell>
                            <TableCell>
                              {mov.payment_method === 'cash' ? (
                                <Badge className="bg-emerald-600 text-white border-0">
                                  <Banknote className="h-3 w-3 mr-1" />
                                  Efectivo
                                </Badge>
                              ) : (
                                <Badge className="bg-purple-600 text-white border-0">
                                  <CreditCard className="h-3 w-3 mr-1" />
                                  Tarjeta
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className={`text-right font-bold ${mov.type === 'expense' ? 'text-red-600' : 'text-emerald-600'}`}>
                              {mov.type === 'expense' ? '-' : '+'}{formatCurrency((mov.amount || 0))}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => reprintHistoryTicket(mov)}
                                title="Reimprimir ticket"
                              >
                                <Printer className="h-4 w-4 text-slate-500 hover:text-emerald-600" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Paginación */}
                  {historyTotal > HISTORY_PAGE_SIZE && (
                    <div className="flex justify-center gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPage <= 1}
                        onClick={() => searchHistory(historyPage - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Anterior
                      </Button>
                      <span className="flex items-center px-4 text-sm text-slate-600">
                        {historyPage} / {Math.ceil(historyTotal / HISTORY_PAGE_SIZE)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPage >= Math.ceil(historyTotal / HISTORY_PAGE_SIZE)}
                        onClick={() => searchHistory(historyPage + 1)}
                      >
                        Siguiente
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: Cierres Pasados */}
        <TabsContent value="closures">
          <Card className={`${darkMode ? 'bg-[#1a1a1a] border-[#333]' : 'border-slate-200'}`}>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                  <Lock className="h-5 w-5" />
                  Histórico de Cierres de Caja
                </CardTitle>
                
                {/* Filtro de fechas */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Botones rápidos */}
                  <div className="flex gap-1">
                    <Button
                      variant={closureFilterType === "today" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleClosureFilterTypeChange("today")}
                      className="h-8"
                    >
                      Hoy
                    </Button>
                    <Button
                      variant={closureFilterType === "week" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleClosureFilterTypeChange("week")}
                      className="h-8"
                    >
                      7 días
                    </Button>
                    <Button
                      variant={closureFilterType === "month" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleClosureFilterTypeChange("month")}
                      className="h-8"
                    >
                      30 días
                    </Button>
                    <Button
                      variant={closureFilterType === "range" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleClosureFilterTypeChange("range")}
                      className="h-8"
                    >
                      <Calendar className="h-3 w-3 mr-1" />
                      Rango
                    </Button>
                  </div>
                  
                  {/* Selector de rango personalizado */}
                  {closureFilterType === "range" && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={closureFilterFrom}
                        onChange={(e) => setClosureFilterFrom(e.target.value)}
                        className="h-8 w-36"
                      />
                      <span className={`text-sm ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>-</span>
                      <Input
                        type="date"
                        value={closureFilterTo}
                        onChange={(e) => setClosureFilterTo(e.target.value)}
                        className="h-8 w-36"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Indicador de filtro activo */}
              <p className={`text-sm mt-2 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                {closureFilterType === "today" && `Mostrando cierres de hoy (${new Date().toLocaleDateString('es-ES')})`}
                {closureFilterType === "week" && "Mostrando cierres de los últimos 7 días"}
                {closureFilterType === "month" && "Mostrando cierres de los últimos 30 días"}
                {closureFilterType === "range" && `Mostrando cierres del ${new Date(closureFilterFrom).toLocaleDateString('es-ES')} al ${new Date(closureFilterTo).toLocaleDateString('es-ES')}`}
                {filteredClosures.length > 0 && ` • ${filteredClosures.length} cierre(s) encontrado(s)`}
              </p>
            </CardHeader>
            <CardContent>
              {historicLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : filteredClosures.length === 0 ? (
                <div className={`text-center py-12 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                  <Lock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No hay cierres registrados para este período</p>
                  <p className="text-sm mt-1">
                    {closureFilterType === "today" 
                      ? "No se han realizado cierres de caja hoy" 
                      : "Prueba a ampliar el rango de fechas"}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Turno</TableHead>
                        <TableHead>Balance Neto</TableHead>
                        <TableHead className="text-right">Efectivo Esp.</TableHead>
                        <TableHead className="text-right">Efectivo Real</TableHead>
                        <TableHead className="text-right">Descuadre</TableHead>
                        <TableHead>Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredClosures.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-semibold">
                            <div className="flex items-center gap-1.5">
                              {c.date ? new Date(c.date).toLocaleDateString('es-ES', { 
                                weekday: 'short', 
                                day: '2-digit', 
                                month: '2-digit',
                                year: '2-digit'
                              }) : '-'}
                              {c.is_auto_closed && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-300" data-testid="auto-close-badge">
                                  Auto
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">#{c.closure_number || 1}</Badge>
                          </TableCell>
                          <TableCell className={`font-bold ${(c.balance_neto_dia || c.total_income - c.total_expense - c.total_refunds || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency((c.balance_neto_dia || (c.total_income || 0) - (c.total_expense || 0) - (c.total_refunds || 0)))}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency((c.expected_cash || 0))}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency((c.physical_cash || 0))}</TableCell>
                          <TableCell className={`text-right font-bold ${(c.discrepancy_total || 0) === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency((c.discrepancy_total || 0))}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="outline" size="sm" onClick={() => printClosingTicket(c)}>
                                <Printer className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                onClick={() => setRevertClosureId(c.id)}
                              >
                                <Undo2 className="h-3 w-3 mr-1" />
                                Reabrir
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ DIALOGS ============ */}

      {/* Open Session Dialog */}
      <Dialog open={showOpenSessionDialog} onOpenChange={setShowOpenSessionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abrir Caja</DialogTitle>
            <DialogDescription>Introduce el fondo de caja inicial</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Fondo de Caja ({getCurrencySymbol()})</Label>
              <Input
                type="number"
                step="0.01"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(e.target.value)}
                placeholder="100.00"
                className="text-xl font-bold mt-1"
                autoFocus
                data-testid="opening-balance-input"
              />
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Textarea
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpenSessionDialog(false)}>Cancelar</Button>
            <Button onClick={openCashSession} data-testid="confirm-open-cash">Abrir Caja</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Movement Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {movementType === "income" ? (
                <><ArrowUpRight className="h-5 w-5 text-emerald-600" /> Nueva Entrada</>
              ) : (
                <><ArrowDownLeft className="h-5 w-5 text-red-600" /> Nueva Salida</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Importe ({getCurrencySymbol()}) *</Label>
              <Input
                type="number"
                step="0.01"
                value={newMovement.amount}
                onChange={(e) => setNewMovement({ ...newMovement, amount: e.target.value })}
                className="text-xl font-bold mt-1"
                placeholder="0.00"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Método de pago *</Label>
                <Select value={newMovement.payment_method} onValueChange={(v) => setNewMovement({ ...newMovement, payment_method: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoría *</Label>
                <Select value={newMovement.category} onValueChange={(v) => setNewMovement({ ...newMovement, category: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecciona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(movementType === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Concepto *</Label>
              <Input
                value={newMovement.concept}
                onChange={(e) => setNewMovement({ ...newMovement, concept: e.target.value })}
                className="mt-1"
                placeholder="Describe la operación..."
              />
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Textarea
                value={newMovement.notes}
                onChange={(e) => setNewMovement({ ...newMovement, notes: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancelar</Button>
            <Button onClick={createMovement} className={movementType === "income" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}>
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Cash Register Dialog - CORRECTED */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Cerrar Caja - Arqueo
            </DialogTitle>
            <DialogDescription>
              Introduce el efectivo y tarjeta contados físicamente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Resumen del día */}
            <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
              <p className="text-sm font-semibold mb-3">📊 Balance del Día</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-slate-500">Ingresos</p>
                  <p className="font-bold text-emerald-600">{formatCurrency((summary?.ingresos_brutos || 0))}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Salidas</p>
                  <p className="font-bold text-red-600">-{formatCurrency((summary?.total_salidas || 0))}</p>
                </div>
                <div className={`p-2 rounded-lg ${(summary?.balance_neto_dia || 0) >= 0 ? 'bg-emerald-500/20' : 'bg-red-100'}`}>
                  <p className="text-xs text-slate-600">Balance Neto</p>
                  <p className={`font-bold text-lg ${(summary?.balance_neto_dia || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {formatCurrency((summary?.balance_neto_dia || 0))}
                  </p>
                </div>
              </div>
            </div>

            {/* Arqueo Efectivo */}
            <div className={`p-4 rounded-xl border-2 ${darkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-500/10 border-blue-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Banknote className="h-5 w-5 text-blue-600" />
                <p className="font-bold text-blue-800">EFECTIVO</p>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Fondo inicial:</span>
                  <span className="font-semibold">{formatCurrency((summary?.opening_balance || 0))}</span>
                </div>
                <div className="flex justify-between items-center text-lg font-bold text-blue-800">
                  <span>Esperado en cajón:</span>
                  <span>{formatCurrency((summary?.efectivo_esperado || 0))}</span>
                </div>
                <div>
                  <Label className="text-blue-700">Efectivo contado físicamente:</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={arqueoForm.physical_cash}
                    onChange={(e) => setArqueoForm({ ...arqueoForm, physical_cash: e.target.value })}
                    className="mt-1 text-lg font-bold"
                    placeholder="0.00"
                    data-testid="physical-cash-input"
                  />
                </div>
                {arqueoForm.physical_cash !== "" && (
                  <div className={`p-2 rounded-lg text-center ${disc.cash === 0 ? 'bg-emerald-500/20' : 'bg-red-100'}`}>
                    <span className={`font-bold ${disc.cash === 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      Descuadre: {formatCurrency(disc.cash)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Arqueo Tarjeta */}
            <div className={`p-4 rounded-xl border-2 ${darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-500/10 border-purple-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-5 w-5 text-purple-600" />
                <p className="font-bold text-purple-800">TARJETA</p>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center text-lg font-bold text-purple-800">
                  <span>Esperado:</span>
                  <span>{formatCurrency((summary?.tarjeta_esperada || 0))}</span>
                </div>
                <div>
                  <Label className="text-purple-700">Total en datáfono:</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={arqueoForm.card_total}
                    onChange={(e) => setArqueoForm({ ...arqueoForm, card_total: e.target.value })}
                    className="mt-1 text-lg font-bold"
                    placeholder="0.00"
                    data-testid="card-total-input"
                  />
                </div>
                {arqueoForm.card_total !== "" && (
                  <div className={`p-2 rounded-lg text-center ${disc.card === 0 ? 'bg-emerald-500/20' : 'bg-red-100'}`}>
                    <span className={`font-bold ${disc.card === 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      Descuadre: {formatCurrency(disc.card)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Notas */}
            <div>
              <Label>Notas del cierre (opcional)</Label>
              <Textarea
                value={arqueoForm.notes}
                onChange={(e) => setArqueoForm({ ...arqueoForm, notes: e.target.value })}
                className="mt-1"
                rows={2}
                placeholder="Incidencias, observaciones..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>Cancelar</Button>
            <Button onClick={closeCashRegister} className="bg-red-600 hover:bg-red-700" data-testid="confirm-close-cash">
              Cerrar Caja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert Closure Confirmation */}
      <Dialog open={!!revertClosureId} onOpenChange={() => setRevertClosureId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Reabrir este cierre?</DialogTitle>
            <DialogDescription>
              Esta acción eliminará el registro de cierre y reabrirá la sesión de caja.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevertClosureId(null)}>Cancelar</Button>
            <Button onClick={confirmRevertClosure} className="bg-orange-600 hover:bg-orange-700">
              Reabrir Caja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Method Dialog */}
      <Dialog open={showEditPaymentDialog} onOpenChange={setShowEditPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Cambiar Método de Pago
            </DialogTitle>
            <DialogDescription>
              Modifica el método de pago de esta operación
            </DialogDescription>
          </DialogHeader>
          {editingMovement && (
            <div className="space-y-4 py-4">
              {/* Movement Info */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Ticket:</span>
                    <span className="ml-2 font-bold text-blue-600">{editingMovement.operation_number || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Importe:</span>
                    <span className={`ml-2 font-bold ${
                      editingMovement.movement_type === 'income' ? 'text-emerald-600' : 
                      editingMovement.movement_type === 'refund' ? 'text-orange-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(editingMovement.amount)}
                    </span>
                  </div>
                </div>
                <div className="mt-2 text-sm">
                  <span className="text-slate-500">Concepto:</span>
                  <span className="ml-2">{editingMovement.concept}</span>
                </div>
              </div>

              {/* Current vs New Payment Method */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                  <Label className="text-xs text-slate-500">Método Actual</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {editingMovement.payment_method === 'cash' ? (
                      <Banknote className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <CreditCard className="h-4 w-4 text-blue-600" />
                    )}
                    <span className="font-semibold">
                      {PAYMENT_METHODS.find(p => p.value === editingMovement.payment_method)?.label}
                    </span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg border-2 ${
                  newPaymentMethod !== editingMovement.payment_method 
                    ? 'border-blue-500 bg-blue-500/10' 
                    : darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                }`}>
                  <Label className="text-xs text-slate-500">Nuevo Método</Label>
                  <Select value={newPaymentMethod} onValueChange={setNewPaymentMethod}>
                    <SelectTrigger className="mt-1 border-0 p-0 h-auto">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => (
                        <SelectItem key={m.value} value={m.value}>
                          <div className="flex items-center gap-2">
                            {m.value === 'cash' ? (
                              <Banknote className="h-4 w-4" />
                            ) : (
                              <CreditCard className="h-4 w-4" />
                            )}
                            {m.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {newPaymentMethod !== editingMovement.payment_method && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-200">
                  <p className="text-sm text-amber-800">
                    ⚠️ Este cambio afectará al desglose por método de pago en el arqueo de caja.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditPaymentDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={updatePaymentMethod}
              disabled={newPaymentMethod === editingMovement?.payment_method}
              data-testid="confirm-edit-payment"
            >
              Guardar Cambio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement detail dialog — extracted to a separate component */}
      <MovementDetailDialog
        open={showHistoryDetailDialog}
        onOpenChange={setShowHistoryDetailDialog}
        movement={selectedHistoryMovement}
        onReprint={reprintHistoryTicket}
      />
    </div>
  );
}
